import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { authService } from '../services/apiService';

const ProfileContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fb;
  padding: 2rem 1rem;
`;

const Header = styled.div`
  max-width: 800px;
  margin: 0 auto 2.5rem;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  padding: 3rem 2.5rem;
  border-radius: 14px;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.15);
  color: white;

  h1 {
    margin: 0;
    color: white;
    font-size: 2rem;
    font-weight: 800;
  }

  p {
    margin: 0.5rem 0 0 0;
    color: rgba(255, 255, 255, 0.85);
  }
`;

const ProfileContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  h2 {
    margin: 0 0 1.5rem 0;
    color: #333;
    font-size: 1.4rem;
    border-bottom: 2px solid #2ecc71;
    padding-bottom: 1rem;
  }
`;

const ProfileField = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  label {
    display: block;
    font-weight: 600;
    color: #666;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  value {
    display: block;
    color: #333;
    font-size: 1rem;
    font-weight: 500;
  }

  a {
    color: #667eea;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  p {
    color: #666;
    font-size: 0.95rem;
  }
`;

const ErrorContainer = styled.div`
  background: #ffebee;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #f44336;
  color: #c62828;

  p {
    margin: 0;
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    if (props.role === 'Seller') return '#4caf50';
    if (props.role === 'Buyer') return '#667eea';
    if (props.role === 'Admin') return '#ff9800';
    return '#999';
  }};
  color: white;
`;

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = localStorage.getItem('userId');

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    if (!userId) {
      setError('User ID not found. Please login first.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await authService.getUserProfile(userId);
      setProfile(data);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <Header>
          <h1>User Profile</h1>
          <p>Loading your information...</p>
        </Header>
        <ProfileContent>
          <LoadingContainer>
            <p>Loading profile data...</p>
          </LoadingContainer>
        </ProfileContent>
      </ProfileContainer>
    );
  }

  if (error) {
    return (
      <ProfileContainer>
        <Header>
          <h1>User Profile</h1>
          <p>Error loading profile</p>
        </Header>
        <ProfileContent>
          <ErrorContainer>
            <p>❌ {error}</p>
          </ErrorContainer>
        </ProfileContent>
      </ProfileContainer>
    );
  }

  if (!profile) {
    return (
      <ProfileContainer>
        <Header>
          <h1>User Profile</h1>
          <p>No profile data available</p>
        </Header>
        <ProfileContent>
          <ErrorContainer>
            <p>❌ No profile data found</p>
          </ErrorContainer>
        </ProfileContent>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <Header>
        <h1>👤 User Profile</h1>
        <p>View your account information</p>
      </Header>

      <ProfileContent>
        {/* User Basic Info */}
        <ProfileCard>
          <h2>Account Information</h2>
          <ProfileField>
            <label>User ID</label>
            <value>{profile.userId}</value>
          </ProfileField>
          <ProfileField>
            <label>Role</label>
            <value><RoleBadge role={profile.role}>{profile.role}</RoleBadge></value>
          </ProfileField>
        </ProfileCard>

        {/* Admin Profile */}
        {profile.role === 'Admin' && profile.adminProfile && (
          <ProfileCard>
            <h2>Admin Profile</h2>
            <ProfileField>
              <label>First Name</label>
              <value>{profile.adminProfile.firstName || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Last Name</label>
              <value>{profile.adminProfile.lastName || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Email</label>
              <value>{profile.adminProfile.email || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Phone Number</label>
              <value>{profile.adminProfile.phoneNumber || 'N/A'}</value>
            </ProfileField>
          </ProfileCard>
        )}

        {/* Seller Profile */}
        {profile.role === 'Seller' && profile.sellerProfile && (
          <ProfileCard>
            <h2>Seller Profile</h2>
            <ProfileField>
              <label>Seller ID</label>
              <value>{profile.sellerProfile.sellerId}</value>
            </ProfileField>
            <ProfileField>
              <label>Seller Name</label>
              <value>{profile.sellerProfile.sellerName || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Email</label>
              <value>{profile.sellerProfile.email || profile.email || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Phone Number</label>
              <value>{profile.sellerProfile.phoneNumber || profile.phoneNumber || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>District</label>
              <value>{profile.sellerProfile.district || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>State</label>
              <value>{profile.sellerProfile.state || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Region</label>
              <value>{profile.sellerProfile.region || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>PAN</label>
              <value>{profile.sellerProfile.pan || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>GSTIN</label>
              <value>{profile.sellerProfile.gstin || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Bank Account</label>
              <value>{profile.sellerProfile.bankAccountNumber || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>IFSC Code</label>
              <value>{profile.sellerProfile.ifscCode || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>KYC Status</label>
              <value>{profile.sellerProfile.kycStatus || 'N/A'}</value>
            </ProfileField>
          </ProfileCard>
        )}

        {/* Buyer Profile */}
        {profile.role === 'Buyer' && profile.buyerProfile && (
          <ProfileCard>
            <h2>Buyer Profile</h2>
            <ProfileField>
              <label>Buyer ID</label>
              <value>{profile.buyerProfile.buyerId}</value>
            </ProfileField>
            <ProfileField>
              <label>Buyer Name</label>
              <value>{profile.buyerProfile.buyerName || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Email</label>
              <value>{profile.buyerProfile.email || profile.email || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Phone Number</label>
              <value>{profile.buyerProfile.phoneNumber || profile.phoneNumber || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Buyer Type</label>
              <value>{profile.buyerProfile.buyerType || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Company Name</label>
              <value>{profile.buyerProfile.companyName || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Business Category</label>
              <value>{profile.buyerProfile.businessCategory || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Address Line 1</label>
              <value>{profile.buyerProfile.addressLine1 || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Address Line 2</label>
              <value>{profile.buyerProfile.addressLine2 || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>District</label>
              <value>{profile.buyerProfile.district || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>State</label>
              <value>{profile.buyerProfile.state || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Region</label>
              <value>{profile.buyerProfile.region || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>Pincode</label>
              <value>{profile.buyerProfile.pincode || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>PAN</label>
              <value>{profile.buyerProfile.pan || 'N/A'}</value>
            </ProfileField>
            <ProfileField>
              <label>GST</label>
              <value>{profile.buyerProfile.gst || 'N/A'}</value>
            </ProfileField>
          </ProfileCard>
        )}
      </ProfileContent>
    </ProfileContainer>
  );
};

export default UserProfile;
